import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Pagination from '@/components/base/Pagination';
import { news } from '@/mocks/news';

const PAGE_SIZE = 6;

export default function News() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tag, setTag] = useState('');
  const page = Number(searchParams.get('page')) || 1;

  const tags = useMemo(() => Array.from(new Set(news.map((n) => n.tag))), []);

  const filtered = useMemo(
    () => (tag ? news.filter((n) => n.tag === tag) : news),
    [tag]
  );

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const changePage = (p: number) => {
    const next = new URLSearchParams(searchParams);
    if (p > 1) next.set('page', String(p));
    else next.delete('page');
    setSearchParams(next);
  };

  return (
    <>
      <Seo
        title="影视资讯专题 - 奸情影视大全"
        description="奸情影视大全影视资讯专题，聚焦情感纠葛题材影视作品的最新动态、剧情解析与深度评论，及时掌握相关影视资讯。"
        keywords="影视资讯,剧情解析,影视评论,奸情影视,影视动态"
      />
      <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-12">
        <nav className="mb-5 flex items-center gap-2 text-xs text-foreground-500 md:text-sm" aria-label="面包屑导航">
          <Link to="/" className="transition-colors hover:text-foreground-950">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">影视资讯</span>
        </nav>

        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">影视资讯专题</h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            聚焦情感纠葛题材影视作品的最新动态、剧情解析与深度评论，帮你及时了解相关作品与行业资讯。
          </p>
        </div>

        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setTag('');
              changePage(1);
            }}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              tag === '' ? 'bg-primary-500 text-foreground-950' : 'bg-secondary-200 text-secondary-950 hover:bg-secondary-300'
            }`}
          >
            全部
          </button>
          {tags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                setTag(t);
                changePage(1);
              }}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                tag === t ? 'bg-primary-500 text-foreground-950' : 'bg-secondary-200 text-secondary-950 hover:bg-secondary-300'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {pageItems.length > 0 ? (
          <div className="flex flex-col gap-5">
            {pageItems.map((item, i) => (
              <Link
                key={item.id}
                to={`/news/detail/${item.id}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-500 sm:flex-row md:p-5"
              >
                <div className="h-44 w-full shrink-0 overflow-hidden rounded-md sm:h-40 sm:w-64">
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title}资讯配图`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="rounded bg-accent-500/15 px-2 py-0.5 text-xs font-medium text-accent-400">
                      {item.tag}
                    </span>
                    <span className="text-xs text-foreground-500">{item.date}</span>
                    <span className="text-xs text-foreground-500">· {item.author}</span>
                  </div>
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 transition-colors group-hover:text-primary-300 md:text-xl">
                    {item.title}
                  </h2>
                  <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
                    {item.summary}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent-400">
                    阅读全文
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
              <i className="ri-newspaper-line text-3xl"></i>
            </span>
            <h3 className="mt-5 font-heading text-lg font-semibold text-foreground-950">暂无相关资讯</h3>
            <p className="mt-2 max-w-sm text-sm text-foreground-600">
              该分类下暂时没有资讯，欢迎返回首页关注最新影视动态。
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line"></i>
              返回首页
            </Link>
          </div>
        )}

        <Pagination currentPage={safePage} totalPages={totalPages} onChange={changePage} />
      </div>
    </>
  );
}